// ============================================================
// TILT — SET PIECES 3D   (src/SetPieces3D.js)   caP'n, 2026-10-25
// ------------------------------------------------------------
// THE BLENDER LAYER. Playfield3D's header used to say "No .glb, no
// Blender, no asset pipeline." That was a hold, not a law (art-style
// ruling, 2026-09). This is the one door a Blender model walks
// through into the table — and it is a LAYER, not a dependency:
//
//   · PHYSICS NEVER SEES IT. A piece is placed AT a collider that
//     already exists (the boss at the `heart`, a bumper body at a
//     `bumper`…). Remove this file and the table plays identically.
//   · FAIL-SAFE (Terror's sculptGate law, same spirit): every piece
//     sits in its own error boundary + Suspense. A missing .glb, a
//     bad file, a slow network → that piece is simply not there and
//     the code-drawn version underneath carries on. The code-drawn
//     bumper/post steps aside ONLY once the Blender one has loaded
//     (see useHave) — never before, never if it failed.
//   · NOTHING BETWEEN THE LENS AND THE BALL (laws-and-practices):
//     a boss stands on deck the ball can reach, so WILLIE FADES to a
//     ghost while a ball is hiding in the strip of deck behind him.
//
// THE MASTERS (built in Blender: gamemaster/models/sculpt/builders/
// tilt_cirque_blender/) — each its OWN glb, 1 unit = 1 table unit,
// origin at the piece's centre on the deck:
//     tilt_bumper.glb   a tent on a stone drum     (at every `bumper`)
//     tilt_post.glb     a stone peg, brass cap     (at every `post`, scaled r/7)
//     tilt_stage.glb    Willie's stage + machinery (at the `heart`)
//     willie.glb        the boss himself           (Terror's ONE Willie)
//     tilt_wall*.glb    one wall chunk, 3 paints   (INSTANCED at every wall/rail/sling/ride-rail chunk)
//     tilt_cabinet.glb  the circus wagon the table sits in (400x700 master; scaled W/400 · H/700)
//     tilt_flipper.glb  the bats                   (at every `engine.flippers[i]`, scaled L/62 · r/6)
// Naming contract is in sculptKit.js (glow_*, glass_*, bs_*).
//
// ⚠ PF PALETTE ONLY. Inside the glass. (TILT Law 6.)
// ⚠ This file never WRITES the World. It READS the engine (balls,
//   fx, table.lamps) through engineRef, exactly as Balls/Pops do.
//   (TILT Law 5.)
// ============================================================
import { Component, Suspense, useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import { useFrame, useLoader } from "@react-three/fiber";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { prepareSculpt, toonRamp } from "./sculptKit";

const BASE = process.env.PUBLIC_URL || "";
const MODEL = (f) => `${BASE}/models/${f}`;
export const WILLIE_URL = MODEL("willie.glb");
const URL_BUMPER = MODEL("tilt_bumper.glb");
const URL_POST = MODEL("tilt_post.glb");
const URL_STAGE = MODEL("tilt_stage.glb");
const URL_FLIPPER = MODEL("tilt_flipper.glb");
const URL_CABINET = MODEL("tilt_cabinet.glb");
const URL_TRAIN = MODEL("tilt_train.glb");

// ── THE WALLS — which paint job a wall chunk's TAG wears ─────────
// (the tags are the ones SKIN_TINT paints for the cirque; anything not listed stays code-drawn,
//  and a ghostable wall always stays code-drawn: its see-through look is the lock's whole promise)
export const WALL_VARIANT = {
  wall: "wall", slingshot: "red", loop: "red",
  ramp: "brass", orbit: "brass", coaster: "brass", rail: "brass",
};
const WALL_URL = { wall: MODEL("tilt_wall.glb"), brass: MODEL("tilt_wall_brass.glb"), red: MODEL("tilt_wall_red.glb") };
const WALL_LEN = 20, WALL_THICK = 6, WALL_VIS = 16;     // the master's own size (see tilt_board.py)

// ── THE BOSS'S SIZE ──────────────────────────────────────────
// Read by the meshes AND by the camera ceiling. THE MARQUEE LAW:
// dressing is part of the machine. Raise these and ceilOf() in
// Playfield3D follows because it reads BOSS_TOP.
export const BOSS_H = 40;                      // Willie, table units tall (a ball is 14 across: ~3 balls — a real table's little figure)
export const STAGE_TOP = 6.8;                  // where Willie's feet are (the stage glb's top plate)
export const BOSS_TOP = BOSS_H + STAGE_TOP;    // the tallest thing on the table that is NOT a wall
const WILLIE_TOP = 1.4;                        // willie.glb's own height unit (MODEL_TOP in Terror)
const STAGE_GLB_R = 15.6;                      // the radius tilt_stage.glb was modelled at
const STAGE_R_DEFAULT = STAGE_GLB_R;            // a table with no solid `stage` collider gets the glb at its own size

// where the boss stands: the table's own `heart` collider, else the middle
export function bossAnchor(eng) {
  const h = eng?.world?.colliders?.find((c) => c.tag === "heart");
  const W = eng?.table?.W ?? 400, H = eng?.table?.H ?? 700;
  return h ? { x: h.c.x, y: h.c.y, r: h.r } : { x: W / 2, y: H / 2, r: 15 };
}

// how wide the stage is: the table's own solid `stage` collider (W3 Cirque: r20), read from the
// physics so the 3D stage can never drift from the thing the ball actually bounces off
export function stageRadius(eng) {
  const st = eng?.world?.colliders?.find((c) => c.id === "stage" && c.kind === "circle");
  return st ? st.r : STAGE_R_DEFAULT;
}

// ── WHICH PIECES HAVE LANDED ─────────────────────────────────
// A tiny module store (React context does not cross <Canvas>; same
// reason sculptGate is a module). Playfield3D reads it so the code-
// drawn bumpers/posts step aside only when the Blender ones are here.
let have = Object.freeze({});
const subs = new Set();
const setHave = (name, v) => {
  if (!!have[name] === v) return;
  have = Object.freeze({ ...have, [name]: v });
  subs.forEach((f) => f());
};
const subscribe = (f) => { subs.add(f); return () => subs.delete(f); };
export const useHave = () => useSyncExternalStore(subscribe, () => have, () => have);

class Door extends Component {
  constructor(p) { super(p); this.state = { bad: false }; }
  static getDerivedStateFromError() { return { bad: true }; }
  componentDidCatch(e) { console.warn(`[set] ${this.props.what} did not load — the code-drawn table stands in`, e); }
  render() { return this.state.bad ? (this.props.fallback ?? null) : this.props.children; }
}

// ── WHAT THE TABLE IS DOING, as the dressing needs to know it ───
// phase 0 dormant / 1 stoking / 2 awake / 3 meltdown (the table's own heat ladder),
// and whether the boss has been felled. READ-ONLY — the same two reads Lamps and
// the HUD already make (table.lamps, engine.boss).
function bossState(e) {
  const L = e && e.table.lamps ? e.table.lamps(e) : {};
  return { phase: Math.max(0, Math.min(3, L.bossPhase | 0)), felled: !!(e && e.boss && e.boss.felled) };
}
const ease = (t) => t * t * (3 - 2 * t);

// ── one instance of a master (shared geometry + shared materials; each
//    instance gets its OWN glow materials so every bumper can pulse alone)
const GLOW = /^(glow_|bs_glow)/;
function instance(S) {
  const root = S.root.clone(true);
  const glows = [];
  root.traverse((o) => {
    if (o.isMesh && GLOW.test(o.name)) {
      o.material = o.material.clone();
      o.userData.c0 = o.material.color.clone();
      glows.push(o);
    }
  });
  return { root, glows };
}
const _w = new THREE.Color(1, 1, 1);
function lightGlows(glows, level, flash) {
  for (const g of glows) {
    g.material.color.copy(g.userData.c0).multiplyScalar(level).lerp(_w, flash);
  }
}

// every consumer keeps its OWN cursor into the engine's fx queue — a
// renderer does not get to empty a queue somebody else may want.
function useFx(engineRef, onFx) {
  const cursor = useRef(0);
  return () => {
    const e = engineRef.current;
    if (!e) return;
    for (const f of e.fx) {
      if (f.key <= cursor.current) continue;
      cursor.current = f.key;
      if (f.at) onFx(f);
    }
  };
}

// ══════════════════════════════════════════════════════════════
//  THE BUMPERS — a tent on a stone drum. The pop is the best-
//  feeling thing on any table; the whole tent flinches and the
//  bulbs flash white when the ball hits it.
// ══════════════════════════════════════════════════════════════
function Bumpers({ geo, engineRef }) {
  const gltf = useLoader(GLTFLoader, URL_BUMPER);
  const S = useMemo(() => prepareSculpt(gltf.scene, { ink: 0.7, selfLight: 0.42 }), [gltf]);
  const { tx, tz } = geo;
  const items = useMemo(
    () => geo.specials.filter((s) => s.tag === "bumper").map((s, i) => ({ s, i, punch: 0, ...instance(S) })),
    [geo, S]
  );
  useEffect(() => { setHave("bumper", true); return () => setHave("bumper", false); }, []);
  useEffect(() => () => S.mats.forEach((m) => m.dispose()), [S]);

  const t = useRef(0);
  const drain = useFx(engineRef, (f) => {
    if (f.fx !== "pop") return;
    for (const it of items) {
      if (Math.hypot(f.at.x - it.s.x, f.at.y - it.s.y) < it.s.r + 14) it.punch = 1;
    }
  });
  useFrame((_, dtRaw) => {
    const dt = Math.min(dtRaw || 0.016, 0.05);
    t.current += dt;
    drain();
    for (const it of items) {
      it.punch = Math.max(0, it.punch - dt * 5);
      const k = 1 + 0.1 * it.punch;
      it.root.scale.set(k, 1 - 0.06 * it.punch, k);
      lightGlows(it.glows, 0.78 + 0.22 * Math.sin(t.current * 2.4 + it.i * 1.7), it.punch * 0.85);
    }
  });
  return (
    <group>
      {items.map((it) => (
        <primitive key={it.s.id} object={it.root} position={[tx(it.s.x), it.s.base, tz(it.s.y)]} rotation={[0, it.i * 0.9, 0]} />
      ))}
    </group>
  );
}

// ══════════════════════════════════════════════════════════════
//  THE POSTS — stone pegs with a brass cap and a gem. The master is
//  r 7 (the standard peg); a hub (r 9) is the same peg, bigger.
// ══════════════════════════════════════════════════════════════
function Posts({ geo }) {
  const gltf = useLoader(GLTFLoader, URL_POST);
  const S = useMemo(() => prepareSculpt(gltf.scene, { ink: 0.6, selfLight: 0.42 }), [gltf]);
  const { tx, tz, heightAt } = geo;
  const items = useMemo(
    () => geo.parts.filter((p) => p.kind === "cyl" && p.tag === "post").map((p, i) => ({ p, i, ...instance(S) })),
    [geo, S]
  );
  useEffect(() => { setHave("post", true); return () => setHave("post", false); }, []);
  useEffect(() => () => S.mats.forEach((m) => m.dispose()), [S]);
  const t = useRef(0);
  useFrame((_, dt) => {
    t.current += Math.min(dt || 0.016, 0.05);
    for (const it of items) lightGlows(it.glows, 0.8 + 0.2 * Math.sin(t.current * 1.9 + it.i * 2.1), 0);
  });
  return (
    <group>
      {items.map((it) => (
        <primitive key={it.p.id} object={it.root} scale={it.p.r / 7}
          position={[tx(it.p.x), heightAt(it.p.x, it.p.y), tz(it.p.y)]} rotation={[0, it.i * 0.7, 0]} />
      ))}
    </group>
  );
}

// ── THE STAGE ────────────────────────────────────────────────
// A stone octagon with two brass cogs and a ring of lamps that TURN with
// the table's heat. Heat → speed: dormant is still (the table may never
// swing first — Law 27), stoking ticks over, awake spins, meltdown races.
const SPIN = [0, 0.5, 1.6, 4.2];            // rad/s by boss phase (0 dormant … 3 meltdown)
// At MELTDOWN the machinery comes UNBOUND: the cogs lift off their axles and wobble, the lamp ring
// levitates and races. When the boss is felled it all settles back and goes quiet.
function StageGlb({ engineRef }) {
  const gltf = useLoader(GLTFLoader, URL_STAGE);
  const S = useMemo(() => prepareSculpt(gltf.scene, { ink: 0.9, selfLight: 0.42 }), [gltf]);
  const inst = useMemo(() => instance(S), [S]);
  const bits = useMemo(() => {
    const o = {};
    inst.root.traverse((n) => { if (/^bs_/.test(n.name)) o[n.name] = n; });
    return o;
  }, [inst]);
  useEffect(() => () => S.mats.forEach((m) => m.dispose()), [S]);
  const spin = useRef(0), t = useRef(0), loose = useRef(0);
  const y0 = useMemo(() => ({ a: bits.bs_cogA?.position.y ?? 0, b: bits.bs_cogB?.position.y ?? 0, r: bits.bs_glowring?.position.y ?? 0 }), [bits]);
  useFrame((_, dtRaw) => {
    const dt = Math.min(dtRaw || 0.016, 0.05);
    t.current += dt;
    const { phase: ph, felled } = bossState(engineRef.current);
    const eff = felled ? 0 : ph;                                           // a felled boss: the machine dies down
    spin.current += (SPIN[eff] - spin.current) * Math.min(1, dt * 2);      // ease: the machine spools up
    loose.current += ((eff === 3 ? 1 : 0) - loose.current) * Math.min(1, dt * 3);
    const u = ease(loose.current), w = t.current;
    if (bits.bs_cogA) {
      bits.bs_cogA.rotation.y += spin.current * dt;
      bits.bs_cogA.position.y = y0.a + 2.6 * u + 0.5 * u * Math.sin(w * 7);
      bits.bs_cogA.rotation.x = 0.28 * u * Math.sin(w * 5);
    }
    if (bits.bs_cogB) {
      bits.bs_cogB.rotation.y -= spin.current * dt;                         // meshing gears turn opposite ways
      bits.bs_cogB.position.y = y0.b + 2.6 * u + 0.5 * u * Math.sin(w * 7 + 2);
      bits.bs_cogB.rotation.z = 0.28 * u * Math.sin(w * 5 + 1.3);
    }
    if (bits.bs_glowring) {
      bits.bs_glowring.rotation.y += spin.current * 0.45 * dt;
      bits.bs_glowring.position.y = y0.r + 5.5 * u + 0.8 * u * Math.sin(w * 3);
    }
    lightGlows(inst.glows, 0.72 + 0.1 * eff + 0.18 * Math.sin(w * (2 + eff)), eff === 3 ? 0.25 : 0);
  });
  return <primitive object={inst.root} />;
}

// the plain stone seat Willie stood on before the Blender stage existed —
// and what he stands on again if the stage glb ever fails to load.
function Plinth({ r = STAGE_R_DEFAULT }) {
  const geo = useMemo(() => {
    const g = new THREE.CylinderGeometry(r * 0.95, r * 1.05, STAGE_TOP, 8, 1);
    g.rotateY(Math.PI / 8);
    return g;
  }, [r]);
  const mat = useMemo(() => new THREE.MeshToonMaterial({ color: "#6f6470", gradientMap: toonRamp(), emissive: "#2a2230" }), []);
  useEffect(() => () => { geo.dispose(); mat.dispose(); }, [geo, mat]);
  return <mesh geometry={geo} material={mat} position={[0, STAGE_TOP / 2, 0]} castShadow receiveShadow />;
}

// ── WILLIE, THE BOSS ─────────────────────────────────────────
function Willie({ geo, engineRef, anchor }) {
  const gltf = useLoader(GLTFLoader, WILLIE_URL);
  const k = BOSS_H / WILLIE_TOP;
  const S = useMemo(
    () => prepareSculpt(gltf.scene, { noInk: /eyes|tears|mouth/, ink: 1.15 / k, selfLight: 0.34 }),
    [gltf, k]
  );
  useEffect(() => () => S.mats.forEach((m) => m.dispose()), [S]);

  const { tx, tz, heightAt } = geo;
  const base = heightAt(anchor.x, anchor.y);
  const fade = useRef(1);
  const grp = useRef(), rise = useRef(0);

  // THE SEE-THROUGH LAW: a ball in the strip of deck directly behind
  // Willie (as the play lens sees it) would be hidden by his body.
  // Fade him to a ghost while that is true, and back when it clears.
  useFrame((_, dtRaw) => {
    const e = engineRef.current;
    if (!e) return;
    const dt = Math.min(dtRaw || 0.016, 0.05);
    // ── HIS APPEARANCE. He is INSIDE the stage while the table is dormant or stoking
    //    (the table may never swing first — and a boss nobody has woken is not there),
    //    rises out of the hatch when the table is AWAKE, stays up through MELTDOWN,
    //    and sinks back for good when he is felled. The stage's solid top plate hides
    //    him below it, so there is no hole to model: he just comes up through the lid.
    const { phase, felled } = bossState(e);
    const want_up = !felled && phase >= 2 ? 1 : 0;
    rise.current += (want_up - rise.current) * Math.min(1, dt * (want_up ? 1.6 : 2.4));
    const r = ease(Math.min(1, Math.max(0, rise.current)));
    if (grp.current) {
      grp.current.visible = r > 0.015;
      grp.current.position.y = STAGE_TOP - (1 - r) * (BOSS_H + 3);
    }
    let hidden = false;
    const reach = BOSS_TOP * 0.85;             // how far behind him a ball can hide (49° lens)
    for (const b of e.world.balls) {
      if (!b.alive) continue;
      const dx = Math.abs(b.p.x - anchor.x), dy = b.p.y - anchor.y;   // dy<0 = behind him
      if (dx < 20 && dy < 6 && dy > -reach) { hidden = true; break; }
    }
    const want = hidden ? 0.26 : 1;
    fade.current += (want - fade.current) * Math.min(1, dt * 9);
    for (const m of S.mats) m.opacity = fade.current;
  });

  return (
    <group position={[tx(anchor.x), base, tz(anchor.y)]}>
      <group ref={grp} position={[0, STAGE_TOP - (BOSS_H + 3), 0]} scale={k} visible={false}>
        <primitive object={S.root} />
      </group>
    </group>
  );
}

function Stage({ geo, engineRef, anchor }) {
  const { tx, tz, heightAt } = geo;
  const sr = stageRadius(engineRef.current);   // the solid stage's own radius, read from the physics
  return (
    <group position={[tx(anchor.x), heightAt(anchor.x, anchor.y), tz(anchor.y)]}>
      <Door what="the stage" fallback={<Plinth r={sr} />}>
        <Suspense fallback={<Plinth r={sr} />}>
          <group scale={[sr / STAGE_GLB_R, 1, sr / STAGE_GLB_R]}>{/* wide as the solid stage, same height */}
            <StageGlb engineRef={engineRef} />
          </group>
        </Suspense>
      </Door>
    </group>
  );
}

// ══════════════════════════════════════════════════════════════
//  THE FLIPPERS — a brass bat, crimson rubber band, cream inlay and an
//  amethyst hub. The master is 62 long, r 6; each bat is scaled by its own
//  length and radius, exactly the numbers the PHYSICS uses (the picture
//  must not lie about the collider). Pose + position are the same maths
//  the code-drawn box used — only the body changed. While the button is
//  down the inlay and the gem flash.
// ══════════════════════════════════════════════════════════════
function Flippers({ geo, engineRef }) {
  const gltf = useLoader(GLTFLoader, URL_FLIPPER);
  const S = useMemo(() => prepareSculpt(gltf.scene, { ink: 0.5, selfLight: 0.42 }), [gltf]);
  const { tx, tz, heightAt } = geo;
  const bats = useMemo(() => {
    const e = engineRef.current;
    return e ? e.flippers.map((f) => ({ id: f.id, ...instance(S) })) : [];
  }, [engineRef, geo, S]);
  useEffect(() => { setHave("flipper", true); return () => setHave("flipper", false); }, []);
  useEffect(() => () => S.mats.forEach((m) => m.dispose()), [S]);
  const flash = useRef({});
  useFrame((_, dtRaw) => {
    const e = engineRef.current;
    if (!e) return;
    const dt = Math.min(dtRaw || 0.016, 0.05);
    for (const b of bats) {
      const f = e.flippers.find((q) => q.id === b.id);
      if (!f) continue;
      const mx = (f.pivot.x + f.tip.x) / 2, my = (f.pivot.y + f.tip.y) / 2;
      const L = Math.hypot(f.tip.x - f.pivot.x, f.tip.y - f.pivot.y) || 1;
      b.root.position.set(tx(mx), heightAt(mx, my) + f.radius + 1, tz(my));
      b.root.rotation.y = Math.atan2(-(f.tip.y - f.pivot.y), f.tip.x - f.pivot.x);
      b.root.scale.set(L / 62, f.radius / 6, f.radius / 6);
      const cur = flash.current[b.id] ?? 0;
      const nxt = cur + ((f.pressed ? 1 : 0) - cur) * Math.min(1, dt * 18);
      flash.current[b.id] = nxt;
      lightGlows(b.glows, 0.85 + 0.5 * nxt, 0.7 * nxt);
    }
  });
  return (
    <group>
      {bats.map((b) => <primitive key={b.id} object={b.root} />)}
    </group>
  );
}

// ══════════════════════════════════════════════════════════════
//  THE WALLS — brass-coped, panelled, with a row of marquee bulbs on the
//  cap. A table has hundreds of wall chunks, so each paint job is ONE
//  InstancedMesh per glb part (body, ink hull, bulbs) — nine draw calls for
//  the whole perimeter. Same chunks, same positions the code-drawn boxes
//  used: the picture can't drift from the physics.
// ══════════════════════════════════════════════════════════════
const _m = new THREE.Matrix4(), _q = new THREE.Quaternion(), _e = new THREE.Euler(), _p = new THREE.Vector3(), _s = new THREE.Vector3();
function WallVariant({ geo, variant }) {
  const gltf = useLoader(GLTFLoader, WALL_URL[variant]);
  const S = useMemo(() => prepareSculpt(gltf.scene, { ink: 0.45, selfLight: 0.42 }), [gltf]);
  const { tx, tz } = geo;
  const chunks = useMemo(
    () => geo.parts.filter((p) => p.kind === "box" && !p.ghost && WALL_VARIANT[p.tag] === variant),
    [geo, variant]
  );
  const built = useMemo(() => {
    S.root.updateMatrixWorld(true);
    const group = new THREE.Group();
    const glows = [];
    S.root.traverse((o) => {
      if (!o.isMesh) return;
      const im = new THREE.InstancedMesh(o.geometry, o.material, Math.max(1, chunks.length));
      im.count = chunks.length;
      im.frustumCulled = false;
      const isInk = /_ink$/.test(o.name), isGlow = !!S.glowing[o.name];
      im.castShadow = !isInk && !isGlow; im.receiveShadow = !isInk && !isGlow;
      chunks.forEach((c, i) => {
        _e.set(0, c.rotY, 0); _q.setFromEuler(_e);
        _p.set(tx(c.x), c.top, tz(c.y));
        _s.set(c.len / WALL_LEN, (c.h ?? WALL_VIS) / WALL_VIS, (c.thick || WALL_THICK) / WALL_THICK);
        _m.compose(_p, _q, _s).multiply(o.matrixWorld);
        im.setMatrixAt(i, _m);
      });
      im.instanceMatrix.needsUpdate = true;
      group.add(im);
      if (isGlow) glows.push({ mat: o.material, c0: o.material.color.clone() });
    });
    return { group, glows };
  }, [S, chunks, tx, tz]);
  useEffect(() => { setHave("w_" + variant, true); return () => setHave("w_" + variant, false); }, [variant]);
  useEffect(() => () => built.group.children.forEach((im) => im.dispose()), [built]);
  useEffect(() => () => S.mats.forEach((m) => m.dispose()), [S]);
  const t = useRef(0);
  useFrame((_, dt) => {
    t.current += Math.min(dt || 0.016, 0.05);
    const k = 0.82 + 0.18 * Math.sin(t.current * 1.7);       // the whole marquee breathes
    for (const g of built.glows) g.mat.color.copy(g.c0).multiplyScalar(k);
  });
  return <primitive object={built.group} />;
}

// ══════════════════════════════════════════════════════════════
//  THE CABINET — the circus wagon: lacquered rails with a pennant valance,
//  a marquee bulb row on the cap, brass medallions, cream slats on the inside
//  faces and a stone tower with an amethyst finial at each corner. The code
//  still draws the hollow body plate underneath and the floating banners.
// ══════════════════════════════════════════════════════════════
function CabinetGlb({ geo }) {
  const gltf = useLoader(GLTFLoader, URL_CABINET);
  const S = useMemo(() => prepareSculpt(gltf.scene, { ink: 0.7, selfLight: 0.4 }), [gltf]);
  const inst = useMemo(() => instance(S), [S]);
  useEffect(() => { setHave("cabinet", true); return () => setHave("cabinet", false); }, []);
  useEffect(() => () => S.mats.forEach((m) => m.dispose()), [S]);
  const t = useRef(0);
  useFrame((_, dt) => {
    t.current += Math.min(dt || 0.016, 0.05);
    lightGlows(inst.glows, 0.8 + 0.2 * Math.sin(t.current * 1.3), 0);
  });
  return <primitive object={inst.root} scale={[geo.W / 400, 1, geo.H / 700]} />;
}

// ══════════════════════════════════════════════════════════════
//  THE CIRCUS TRAIN (W4) — laps Willie on an elevated ring held off his
//  stage. The PHYSICS is one moving sensor (`train`, T01) under the
//  locomotive; this only follows it. The master is in GAME units, centred
//  on Willie, loco at +x facing +z, so rotation.y = -angle puts the loco
//  exactly over the sensor. If the glb fails, a plain gold block follows
//  the sensor instead, so the hit zone is never invisible.
// ══════════════════════════════════════════════════════════════
const trainAngle = (eng, anchor) => {
  const tr = eng?.world?.colliders?.find((c) => c.id === "train");
  return tr ? Math.atan2(tr.c.y - anchor.y, tr.c.x - anchor.x) : null;
};
function TrainGlb({ engineRef, anchor }) {
  const gltf = useLoader(GLTFLoader, URL_TRAIN);
  const S = useMemo(() => prepareSculpt(gltf.scene, { ink: 0.8, selfLight: 0.42 }), [gltf]);
  const inst = useMemo(() => instance(S), [S]);
  const spin = useMemo(() => {                     // the bs_train* parts, gathered under one turning group
    const g = new THREE.Group();
    inst.root.updateMatrixWorld(true);
    const movers = [];
    inst.root.traverse((o) => { if (/^bs_train/.test(o.name) && !/^bs_train/.test(o.parent?.name || "")) movers.push(o); });
    inst.root.add(g);
    movers.forEach((o) => g.attach(o));
    return g;
  }, [inst]);
  useEffect(() => () => S.mats.forEach((m) => m.dispose()), [S]);
  const t = useRef(0);
  useFrame((_, dt) => {
    t.current += Math.min(dt || 0.016, 0.05);
    const a = trainAngle(engineRef.current, anchor);
    inst.root.visible = a != null;                 // a table with no train shows no track
    if (a != null) spin.rotation.y = -a;
    const { phase: ph } = bossState(engineRef.current);
    lightGlows(inst.glows, 0.7 + 0.1 * ph + 0.15 * Math.sin(t.current * (2 + ph)), 0);
  });
  return <primitive object={inst.root} />;
}
function TrainStandIn({ geo, engineRef, anchor }) {
  const ref = useRef();
  useFrame(() => {
    const a = trainAngle(engineRef.current, anchor);
    if (!ref.current) return;
    ref.current.visible = a != null;
    if (a == null) return;
    ref.current.position.set(45 * Math.cos(a), 22, 45 * Math.sin(a));
    ref.current.rotation.y = -a;
  });
  return (
    <mesh ref={ref}>
      <boxGeometry args={[4.6, 4, 9]} />
      <meshToonMaterial color="#c9a84c" gradientMap={toonRamp()} />
    </mesh>
  );
}
function Train({ geo, engineRef, anchor }) {
  const { tx, tz, heightAt } = geo;
  return (
    <group position={[tx(anchor.x), heightAt(anchor.x, anchor.y), tz(anchor.y)]}>
      <Door what="the circus train" fallback={<TrainStandIn geo={geo} engineRef={engineRef} anchor={anchor} />}>
        <Suspense fallback={<TrainStandIn geo={geo} engineRef={engineRef} anchor={anchor} />}>
          <TrainGlb engineRef={engineRef} anchor={anchor} />
        </Suspense>
      </Door>
    </group>
  );
}

// ══════════════════════════════════════════════════════════════
//  THE COMPONENT — mounted once inside <Scene>. Cirque only, never
//  the little hostile (opponent's-avatar) table: that is a SECOND
//  WebGL context 260px wide, nobody could see the pieces, and the
//  .glbs are a download we do not make a duel pay twice.
// ══════════════════════════════════════════════════════════════
export default function SetPieces({ geo, engineRef, hostile }) {
  const anchor = useMemo(() => bossAnchor(engineRef.current), [engineRef, geo]);
  if (hostile || geo.skin !== "cirque") return null;
  return (
    <group name="set-pieces">
      <Door what="the bumpers"><Suspense fallback={null}><Bumpers geo={geo} engineRef={engineRef} /></Suspense></Door>
      <Door what="the posts"><Suspense fallback={null}><Posts geo={geo} /></Suspense></Door>
      {["wall", "brass", "red"].map((v) => (
        <Door key={v} what={"the " + v + " walls"}><Suspense fallback={null}><WallVariant geo={geo} variant={v} /></Suspense></Door>
      ))}
      <Door what="the cabinet"><Suspense fallback={null}><CabinetGlb geo={geo} /></Suspense></Door>
      <Door what="the flippers"><Suspense fallback={null}><Flippers geo={geo} engineRef={engineRef} /></Suspense></Door>
      <Stage geo={geo} engineRef={engineRef} anchor={anchor} />
      <Train geo={geo} engineRef={engineRef} anchor={anchor} />
      <Door what="Willie"><Suspense fallback={null}><Willie geo={geo} engineRef={engineRef} anchor={anchor} /></Suspense></Door>
    </group>
  );
}
